import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { MenuItem } from '@material-ui/core'
import { Button } from '@mui/material'
import { SimpleModal } from 'components/Modal'
import FixedLoader from 'components/FixedLoader/FixedLoader'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import Joi from 'joi'
import Api from 'api'
import { usePersistedState, useBlurFocusedInputOnUnmount } from 'hooks'
import { entities } from 'reduxStore'
import { HygienistProps, HYGIENIST_TYPES, Type } from 'utils/types'
import TalentNavbar from 'components/TalentNavbar/TalentNavbar'
import InstabookVideoIframe from 'components/InstabookVideoIframe/InstabookVideoIframe'
import TalentPageLayout from 'components/TalentPageLayout'
import TalentBackButton from 'components/TalentBackButton/TalentBackButton'
import { FormMaterialSelector } from 'components/Selector'
import { FormRadioInputGroup, RadioInput } from 'components/Inputs/RadioInput'
import {
  Actions,
  FieldGroup,
  HelperText,
  Content,
  PageContainer,
  PageTitle,
  RadiosTitle,
  ResponsiveIframeWrap,
  Section,
  Subtitle,
  Title,
} from './AutoBookPage.styles'

type DistanceKm = 25 | 50 | 75 | 110
type MinShiftHours = 4 | 5 | 6 | 7 | 8

interface GlobalSettingsForm {
  hourlyRate: number | ''
  maxDistance: DistanceKm | ''
  minShiftLength: MinShiftHours | ''
}

const schema = Joi.object<GlobalSettingsForm>({
  hourlyRate: Joi.number().required().messages({ 'any.required': 'Hourly rate is required' }),
  maxDistance: Joi.number().valid(25, 50, 75, 110).required().messages({ 'any.only': 'Please select a distance' }),
  minShiftLength: Joi.number().valid(4, 5, 6, 7, 8).required().messages({ 'any.only': 'Please select a minimum shift length' }),
})

const AutoBookPage: React.FC = () => {
  const history = useHistory()
  const [hygienist, setHygienist] = usePersistedState<HygienistProps>({ entityName: entities.hygienist })
  const [user, setUser] = usePersistedState<any>({ entityName: entities.user })

  const [avgRate, setAvgRate] = useState<number | null>(null)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [prefill, setPrefill] = useState<{ hourlyRate?: number; maxDistance?: DistanceKm; minShiftLength?: MinShiftHours } | null>(null)
  const [error, setError] = useState<string>('')

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<GlobalSettingsForm>({
    mode: 'onChange',
    resolver: joiResolver(schema) as any,
    defaultValues: { hourlyRate: '', maxDistance: '', minShiftLength: '' } as any,
  })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const type = (hygienist?.type ?? user?.hygienist?.type) as Type
        const postalCode = hygienist?.postalCode || user?.hygienist?.postalCode
        const countryStr = hygienist?.countryStr || user?.hygienist?.countryStr

        // Try to load from local hygienist.GlobalSettings first as a fallback
        const localGs = (hygienist as any)?.GlobalSettings || (user?.hygienist as any)?.GlobalSettings
        if (localGs && localGs.hourlyRate) {
          setPrefill({ hourlyRate: localGs.hourlyRate, maxDistance: localGs.maxDistance as DistanceKm, minShiftLength: localGs.minShiftLength as MinShiftHours })
          setValue('hourlyRate', localGs.hourlyRate as any)
          setValue('maxDistance', localGs.maxDistance as any)
          setValue('minShiftLength', localGs.minShiftLength as any)
        }

        const [settings, avg, video] = await Promise.all([
          Api.getHygienistSettings().catch(() => ({ GlobalSettings: null })),
          postalCode && (type === 0 || type === 1) ? Api.getAvgRate({ type, postalCode }).catch(() => ({ avgRate: null })) : Promise.resolve({ avgRate: null } as any),
          Api.getInstabookVideoLink().catch(() => ''),
        ])

        if (cancelled) return
        const gs = settings?.GlobalSettings
        if (gs && gs.hourlyRate) {
          setPrefill({ hourlyRate: gs.hourlyRate, maxDistance: gs.maxDistance as DistanceKm, minShiftLength: gs.minShiftLength as MinShiftHours })
          setValue('hourlyRate', gs.hourlyRate as any)
          setValue('maxDistance', gs.maxDistance as any)
          setValue('minShiftLength', gs.minShiftLength as any)
        }
        setAvgRate(Number(avg?.avgRate) || null)
        setVideoUrl(typeof video === 'string' ? video : '')
      } catch (e: any) {
        // Don't show error if we managed to load from local state
        if (!prefill?.hourlyRate) {
          setError(e?.message ? e.message : 'Failed to load settings')
        }
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [hygienist, user, setValue])

  const isUS = (hygienist?.country === 1) || (hygienist?.countryStr === 'US') || (user?.hygienist?.country === 1) || (user?.hygienist?.countryStr === 'US')
  const distanceUnit = isUS ? 'mi' : 'km'

  const rateOptions = useMemo(() => {
    const type = (hygienist?.type ?? user?.hygienist?.type) as Type
    const a = avgRate
    if (!a || isNaN(a)) {
      // fallback range when avg unavailable
      const range = type === 1 ? [18, 40] : [30, 80]
      const list: number[] = []
      for (let r = range[0]; r <= range[1]; r++) list.push(r)
      return { list, recommended: null as number | null }
    }
    const spread = type === 1 ? 6 : 10
    const min = Math.max(0, Math.floor(a - spread))
    const max = Math.floor(a + spread)
    const list: number[] = []
    for (let r = min; r <= max; r++) list.push(r)
    return { list, recommended: Math.round(a) }
  }, [avgRate, hygienist, user])

  const [confirmOpen, setConfirmOpen] = useState(false)

  useBlurFocusedInputOnUnmount()

  const navHygienist = hygienist || user?.hygienist
  const isHygOrAsst =
    navHygienist?.type === HYGIENIST_TYPES.HYGIENIST ||
    navHygienist?.type === HYGIENIST_TYPES.ASSISTANT

  const handleBack = () => {
    history.push({ pathname: 'hygienistHome' })
  }

  const onSubmit = async (data: GlobalSettingsForm) => {
    setError('')
    try {
      const payload = {
        hourlyRate: Number(data.hourlyRate),
        maxDistance: Number(data.maxDistance) as DistanceKm,
        minShiftLength: Number(data.minShiftLength) as MinShiftHours,
      }
      const hasExisting = !!(prefill?.hourlyRate && prefill?.maxDistance && prefill?.minShiftLength)
      if (hasExisting) {
        await Api.updateInstabookSettings(payload)
      } else {
        await Api.setInstabookSettings(payload)
      }
      try { (window as any)?.TempStars?.Analytics?.track?.('Autobook_Settings_Saved', payload) } catch {}
      try {
        const refreshed = await Api.getUser()
        setUser(refreshed as any)
        if ((refreshed as any)?.hygienist) {
          setHygienist(((refreshed as any).hygienist || {}) as any)
        }
        safeLocalStorage.setItem('userAccount', JSON.stringify(refreshed))
      } catch {}
      setConfirmOpen(true)
    } catch (e: any) {
      setError(e?.message ? e.message : 'Failed to save settings')
    }
  }

  return (
    <>
      <TalentPageLayout
        navbar={(
          <TalentNavbar
            firstName={navHygienist?.firstName || ''}
            lastName={navHygienist?.lastName || ''}
            photoUrl={navHygienist?.photoUrl || undefined}
            isHygOrAsst={isHygOrAsst}
            instabookActivated={navHygienist?.InstabookActivated === 1}
            isTestAccount={(navHygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
          />
        )}
      >
      <PageContainer>
      <FixedLoader loading={loading} />
      <TalentBackButton onBack={handleBack} background="#fff" />
      <Content>
      <PageTitle>Autobook Settings</PageTitle>

      <Section>
        <Title>Scheduling Availability Settings</Title>
        <Subtitle>These settings will apply to all dates you indicate as Available.</Subtitle>

        <FieldGroup>
          <FormMaterialSelector
            control={control}
            name="hourlyRate"
            label="Select Hourly Rate"
            placeholder="Select Hourly Rate"
            formatSelectedValue={(selected) => selected ? `$${selected}/hr` : 'Select Hourly Rate'}
          >
            {[
              <MenuItem key="placeholder" value="" disabled>
                Select Hourly Rate
              </MenuItem>,
              ...rateOptions.list.map((r) => (
                <MenuItem key={r} value={r}>
                  {`$${r}/hr`}
                  {rateOptions.recommended !== null && r === rateOptions.recommended ? ' (Recommended)' : ''}
                </MenuItem>
              ))
            ]}
          </FormMaterialSelector>
        </FieldGroup>

        <FieldGroup>
          <RadiosTitle>Minimum Shift Length</RadiosTitle>
          <FormRadioInputGroup name="minShiftLength" control={control}>
            {[4,5,6,7,8].map((h) => (
              <RadioInput key={h} value={h} label={`${h} Hours`} />
            ))}
          </FormRadioInputGroup>
        </FieldGroup>

        <FieldGroup>
          <RadiosTitle>Maximum Distance</RadiosTitle>
          <FormRadioInputGroup name="maxDistance" control={control}>
            {[25,50,75,110].map((d) => (
              <RadioInput key={d} value={d} label={`${d} ${distanceUnit}`} />
            ))}
          </FormRadioInputGroup>
          <HelperText>
            Note Gas/Travel Allowances — 21 - 45{distanceUnit}: $7.50. 46-75{distanceUnit}: $15.00. 76-90{distanceUnit}: $25.00. 91+{distanceUnit}: $30.00
          </HelperText>
        </FieldGroup>

        <Actions>
          <Button
            variant="contained"
            color="primary"
            className="bigButtonInNewStyle"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || loading}
          >
            Submit
          </Button>
        </Actions>
      </Section>

      <Section>
        {videoUrl ? (
          <ResponsiveIframeWrap>
            <InstabookVideoIframe
              url={videoUrl}
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </ResponsiveIframeWrap>
        ) : (
          <Subtitle>Video unavailable at the moment.</Subtitle>
        )}
      </Section>

      {error ? (
        <Section>
          <Subtitle style={{ color: 'var(--primary-main, #FB7672)' }}>{error}</Subtitle>
        </Section>
      ) : null}
      </Content>
      <SimpleModal isOpen={confirmOpen} onClose={() => { setConfirmOpen(false); history.push({ pathname: 'hygienistHome' }) }}>
        <div style={{ padding: '1rem', minWidth: 280 }}>
          <h3 style={{ marginTop: 0 }}>Settings Saved</h3>
          <div style={{ color: 'var(--text-secondary, #666)', marginBottom: '0.75rem' }}>
            Your Auto-book settings are saved. You can now set availability on your calendar.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="bigButtonInNewStyle" onClick={() => { setConfirmOpen(false); history.push({ pathname: 'hygienistHome' }) }}>OK</button>
          </div>
        </div>
      </SimpleModal>
    </PageContainer>
    </TalentPageLayout>
    </>
  )
}

export default AutoBookPage


